import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';

import { useLanguage } from '../../hooks';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { AlertTriangleOutline } from '../../icons';
import { useAppSelector } from '../../store/hooks';
import { SkipLogicQuestion } from '../../types';
import { PhotoQuestionIcon, TextQuestionIcon } from '../icons';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { InfoAlert } from '../uiComponents/infoAlert';
import { barCodeTypes } from './barCodeTypes';
import NumberPercentSetting from './numberPercentSetting';
import { SkipLogicComponent } from './skipLogicComponent';
import TextValidationSetting from './textValidationSetting';

const customStyles: Record<string, SxProps<Theme>> = {
  mainContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  setting: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '22px',
    '&:last-child': {
      marginRight: '0px',
    },
  },
  label: {
    fontSize: '14px',
    lineHeight: '22px',
  },
  expandable: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingLeft: '0.25em',

    '&:hover': {
      backgroundColor: 'rgba(32,33,36,0.039)',
      borderRadius: '3px',
    },
  },
  closeIcon: {
    transition: 'transform .1s ease-in-out',
    transform: 'rotate(180deg)',
    MsTransform: 'rotate(180deg)',
    WebkitTransform: 'rotate(180deg)',
  },
  openIcon: {
    transition: 'transform .1s ease-out',
  },
  popover: {
    '& .MuiPopover-paper': {
      borderRadius: '8px',
    },
  },
  toggletip: {
    backgroundColor: '#FFFFFF',
    color: '#3B3C40',
    maxWidth: '240px',
    maxHeight: '400px',
    fontSize: '14px',
    overflowY: 'auto',
    lineHeight: '22px',
    boxshadow: '2px 4px 16px rgba(15, 59, 96, 0.18)',
    borderRadius: '8px',
    padding: '12px',
  },
  toggletipInfo: {
    fontSize: '14px',
    marginBottom: '16px',
    cursor: 'auto',
  },
  toggletipLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '14px',
      display: '-webkit-box',
      lineHeight: '22px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: '2px',
      WebkitBoxOrient: 'vertical',
      wordWrap: 'break-word',
      lineClamp: '2px',
    },
  },
  commentOptionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  commentAlternatives: {
    flexShrink: 0,
  },
  commentAlternativeBtn: {
    fontSize: 18,
  },
  commentTextBtn: {
    fill: '#B8BABF',
  },
  commentImageBtn: {
    stroke: '#B8BABF',
  },
  commentTextRequired: {
    fill: '#3B3C40',
  },
  commentImageRequired: {
    stroke: '#3B3C40',
  },
  mobileOnlyAlert: {
    margin: '24px 0px',
  },
  textFieldMargin: {
    marginRight: '8px',
  },
  textFieldInputPadding: {
    padding: '4px 8px',
  },
};

const TableQuestionSetting = memo(function TableQuestionSetting(props: {
  tableId: string;
  question: any;
  onRequiredInTableChange: any;
  onTextValidationChange: (
    questionId: string,
    validation: 'email' | 'rut-chile' | 'container' | null,
    tableId: string,
  ) => void;
  onNumberPercentageChange: (
    questionId: string,
    show: 'percent' | 'sampling' | null,
    tableId: string,
  ) => void;
  onBarcodeTypeInTableChange: any;
  onDisableGalleryInTableChange: any;
  onMultiPhotoInTableChange: any;
  numberMetadataInTableSetting: any;
  onRelatedListInTableChange: (id: string, action: string, value: string, tableId: string) => void;
  onOptionsWithCommentsInTableChange: (tableId: string, questionId: string) => void;
  onObligatoryOptionCommentInTableChange: (
    tableId: string,
    id: string,
    index: number,
    reqText: boolean,
    reqImage: boolean,
    reqFinding: boolean,
  ) => void;
  onChecklistSettingsChange: (
    questionId: string,
    disableGallery?: boolean,
    maxImages?: number,
    tableId?: string,
  ) => void;
  onSkipLogicInTableChange: (id: string, tableId: string) => void;
  onSkipSettingInTableChange: (id: string, set: string, value: string, tableId: string) => void;
  onMeterChange: (questionId: string, value: string, tableId?: string) => void;
  onDateValidationCheckboxTableChange: (id: string, tableId: string) => void;
  paying: boolean;
  tableLogicQuestions: { [key: string]: SkipLogicQuestion[] };
}) {
  const {
    tableId,
    question,
    onRequiredInTableChange,
    onTextValidationChange,
    onNumberPercentageChange,
    onBarcodeTypeInTableChange,
    onDisableGalleryInTableChange,
    onMultiPhotoInTableChange,
    numberMetadataInTableSetting,
    onRelatedListInTableChange,
    onOptionsWithCommentsInTableChange,
    onObligatoryOptionCommentInTableChange,
    onChecklistSettingsChange,
    onSkipLogicInTableChange,
    onSkipSettingInTableChange,
    onMeterChange,
    onDateValidationCheckboxTableChange,
    paying,
    tableLogicQuestions,
  } = props;
  const [openedAlert, setOpenedAlert] = useState(false);
  const [checklistMaxImages, setChecklistMaxImages] = useState(question?.maxImages ?? 1);
  const [searchParams] = useSearchParams();

  const lang = useLanguage();

  const { checkFlag, getPremiumFlag } = useFeatureFlags();
  const findingsActivated = !!getPremiumFlag('Findings');
  const multiPhoto = checkFlag('MultiPhotoTable');
  const fileQuestionRequired = checkFlag('FileQuestionRequired');
  const findingsCommentRequiredFlag = checkFlag('FindingsCommentRequired');
  const dateQuestionValidationFlag = checkFlag('DateQuestionValidation');

  const { globalObjectsTotals } = useAppSelector((state) => state.listObjects);
  const { total: totalLocationItems } = useAppSelector((state) => state.locations);
  const { rightColumn } = useAppSelector((state) => state.listManager);
  const { enqueueSnackbar } = useSnackbar();

  const totalOptionItems = useMemo(
    () => globalObjectsTotals[question?.metadata],
    [globalObjectsTotals, question?.metadata],
  );
  const safeToReqImage = useMemo(() => {
    // We need to check the items numbers * the max images are below the max of 500 images
    const noMetadataSelected = !question?.metadata;
    const locationsListSelected = question?.metadata === 'locations';
    const metadataListSelected = question?.metadata && question?.metadata !== 'locations';
    const safeToReqImage =
      noMetadataSelected ||
      (locationsListSelected && (question?.maxImages ?? 10) * totalLocationItems < 500) ||
      (metadataListSelected && (question?.maxImages ?? 10) * totalOptionItems < 500);
    return safeToReqImage;
  }, [totalLocationItems, totalOptionItems, question?.metadata, question?.maxImages]);

  useEffect(() => {
    if (!question?.options) return;
    if (!question?.metadata) return;
    if (globalObjectsTotals[question.metadata] * (question?.maxImages ?? 1) > 500) {
      let atLeastOneImageIsMandatory = false;
      // We remove the mandatory image comments for all the options of the question:
      question.options.map((option: any, index: number) => {
        // Check if option is an object and has the req_text property
        const isObjectAndNotString =
          typeof option === 'object' && option !== null && !(option instanceof String);
        const reqText = isObjectAndNotString && 'req_text' in option ? option.req_text : false;
        const reqImage = isObjectAndNotString && 'req_image' in option ? option.req_image : false;
        atLeastOneImageIsMandatory = atLeastOneImageIsMandatory || reqImage;
        const reqFinding =
          isObjectAndNotString && 'req_finding' in option ? option.req_finding : false;

        onObligatoryOptionCommentInTableChange(
          tableId,
          question.id,
          index,
          reqText,
          false, //default to false
          reqFinding,
        );
      });
      if (atLeastOneImageIsMandatory) {
        enqueueSnackbar(lang.form_builder.imageDisabledByLimit, {
          variant: 'warning',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        });
      }
    }
  }, [globalObjectsTotals, question?.metadata, question?.maxImages]);
  useEffect(() => {
    if (!question?.options) return;
    if (!question?.metadata || question?.metadata !== 'locations') return;
    // For when the locations list is chosen:
    if (totalLocationItems > 50) {
      question.options.map((option: any, index: number) => {
        // Check if option is an object and has the req_text property
        const isObjectAndNotString =
          typeof option === 'object' && option !== null && !(option instanceof String);
        const reqText = isObjectAndNotString && 'req_text' in option ? option.req_text : false;
        const reqFinding =
          isObjectAndNotString && 'req_finding' in option ? option.req_finding : false;
        onObligatoryOptionCommentInTableChange(
          tableId,
          question.id,
          index,
          reqText,
          false,
          reqFinding,
        );
      });
    }
  }, [totalLocationItems, question?.metadata]);

  const [toggletipAnchor, setToggletipAnchor] = useState<HTMLDivElement | null>(null);

  const handleOpenConfigToggletip = (
    event: React.MouseEvent<HTMLDivElement>,
    questionType: string,
    questionId: string,
  ) => {
    if (questionType === 'select_option_metadata') {
      onOptionsWithCommentsInTableChange(tableId, questionId);
    }
    setToggletipAnchor(event.currentTarget);
  };

  const handleObligatoryCommentInTableCheckbox = useCallback(
    (tableId: string, index: number, option: any) => {
      onObligatoryOptionCommentInTableChange(
        tableId,
        question.id,
        index,
        !(option.req_text || option.req_image || option.req_finding),
        safeToReqImage ? !(option.req_text || option.req_image || option.req_finding) : false,
        !(option.req_text || option.req_image || option.req_finding) &&
          findingsActivated &&
          findingsCommentRequiredFlag,
      );
    },
    [safeToReqImage, findingsActivated, findingsCommentRequiredFlag],
  );

  const handleOnChecklistSettingsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, tableId: string) => {
      const newMaxImages =
        parseInt(event.target.value) > 10
          ? '10'
          : parseInt(event.target.value) < 1
          ? '1'
          : event.target.value === ''
          ? '1'
          : event.target.value;
      onChecklistSettingsChange(
        question.id,
        question.disableGallery,
        parseInt(newMaxImages) ?? 1,
        tableId,
      );
      setChecklistMaxImages(newMaxImages);
    },
    [onChecklistSettingsChange],
  );

  const handleCloseConfigToggletip = () => {
    setToggletipAnchor(null);
  };

  const handleSkipLogicChange = () => {
    if (paying) onSkipLogicInTableChange(question.id, tableId);
    else setOpenedAlert(true);
  };

  const openToggleTip = Boolean(toggletipAnchor);
  const toggleTipId = openToggleTip ? 'obligatory-comments' : undefined;

  return (
    <CardContent style={{ padding: '4px 16px 4px' }}>
      <Box sx={customStyles.mainContainer}>
        {[
          'text',
          'number',
          'date',
          'signature',
          'barcode',
          'photo',
          'multi_photo',
          'multi_option',
          'select_metadata',
          'select_number_metadata',
          'select_text_metadata',
          'select_option_metadata',
          'select_option_metadata_comments',
          'select_activity_period_time',
          ...(fileQuestionRequired ? ['attachment'] : []),
        ].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.req === '1'}
              onChange={() => onRequiredInTableChange(question.id, tableId)}
            />
            <Box sx={customStyles.label}>{lang.form_builder.required}</Box>
          </Box>
        )}
        {question.type === 'text' && (
          <TextValidationSetting
            validateMail={question.mail}
            validateRutChile={question.rutValidation}
            validateContainer={question.containerValidation}
            onValidationChange={(validation) =>
              onTextValidationChange(question.id, validation, tableId)
            }
          />
        )}
        {question.type === 'barcode' && (
          <Box sx={{ margin: '0 15px 0' }}>
            <Select
              disableUnderline
              value={question.barcodeType ? question.barcodeType : 'N/A'}
              onChange={(e: any) =>
                onBarcodeTypeInTableChange(question.id, e.target.value, tableId)
              }
              renderValue={(value) => {
                if (value === 'N/A')
                  return (
                    <Tooltip title={lang.form_builder.codeType}>
                      <Box sx={{ fontSize: 12, paddingTop: 5 }}>N/A</Box>
                    </Tooltip>
                  );
                return (
                  <Tooltip title={lang.form_builder.codeType}>
                    <Box sx={{ fontSize: 12, paddingTop: 5 }}>
                      {barCodeTypes.find((type) => type.value === value).tag}
                    </Box>
                  </Tooltip>
                );
              }}
            >
              {barCodeTypes.map((type) => (
                <MenuItem
                  style={{ height: 18 }}
                  value={type.value}
                >
                  <Box sx={{ fontSize: 12, paddingTop: 5 }}>{type.tag}</Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {(question.type === 'photo' || question.type === 'multi_photo') && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.disableGallery === '1'}
                onChange={() => onDisableGalleryInTableChange(question.id, tableId)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.disableGallery}</Box>
            </Box>
            {multiPhoto && (
              <Box sx={customStyles.setting}>
                <Checkbox
                  disableTouchRipple
                  size={'small'}
                  color={'default'}
                  checked={question.type === 'multi_photo'}
                  onChange={() => onMultiPhotoInTableChange(question.id, tableId)}
                />
                <Box sx={customStyles.label}>{lang.form_builder.multi_photo}</Box>
              </Box>
            )}
          </>
        )}
        {searchParams.get('number_count_config') === 'true' &&
          question.type === 'select_number_metadata' && (
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.asCount === '1'}
                onChange={() => numberMetadataInTableSetting(question.id, 'asCount', tableId)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.asCount}</Box>
            </Box>
          )}
        {question.type === 'select_number_metadata' && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showPrice === '1'}
                onChange={() => numberMetadataInTableSetting(question.id, 'showPrice', tableId)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showPrice}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showAvg === '1'}
                onChange={() => numberMetadataInTableSetting(question.id, 'showAvg', tableId)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showAvg}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showFactor === '1'}
                onChange={() => numberMetadataInTableSetting(question.id, 'showFactor', tableId)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showFactor}</Box>
            </Box>
            {checkFlag('ShowSamplingPercent') ? (
              <NumberPercentSetting
                showPercent={question.showPercent === '1'}
                showSampling={question.showSampling === '1'}
                onShowChange={(show) => onNumberPercentageChange(question.id, show, tableId)}
              />
            ) : (
              <Box sx={customStyles.setting}>
                <Checkbox
                  disableTouchRipple
                  size={'small'}
                  color={'default'}
                  checked={question.showPercent === '1'}
                  onChange={() => numberMetadataInTableSetting(question.id, 'showPercent', tableId)}
                />
                <Box sx={customStyles.label}>{lang.form_builder.showPercent}</Box>
              </Box>
            )}
          </>
        )}
        {['multi_option', 'select_metadata'].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.related_list === '1'}
              onChange={() =>
                onRelatedListInTableChange(question.id, 'related:list', undefined, tableId)
              }
            />
            <Box sx={customStyles.label}>{lang.form_builder.relatedList}</Box>
          </Box>
        )}
        {
          /* TODO: In the future we may want to add meter updates to table questions */
          false &&
            checkFlag('Assets') &&
            question.type === 'select_metadata' &&
            rightColumn === 'ASSETS' && (
              <Box sx={customStyles.setting}>
                <Checkbox
                  disableTouchRipple
                  size={'small'}
                  color={'default'}
                  checked={
                    Boolean(question.meterConfigurations?.length) ||
                    !!question.meterId ||
                    question.updateData === '1'
                  }
                  onChange={() => onMeterChange(question.id, '-1', tableId)}
                />
                <Box sx={customStyles.label}>{lang.form_builder.updateAssetData}</Box>
              </Box>
            )
        }
        {['select_option_metadata', 'select_option_metadata_comments'].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.type === 'select_option_metadata_comments'}
              onChange={() => onOptionsWithCommentsInTableChange(tableId, question.id)}
            />
            <Box
              sx={customStyles.expandable}
              onClick={(e) => handleOpenConfigToggletip(e, question.type, question.id)}
            >
              <Box sx={customStyles.label}>{lang.form_builder.includeComments}</Box>
              <ExpandMoreIcon sx={openToggleTip ? customStyles.closeIcon : customStyles.openIcon} />
            </Box>
            <Popover
              id={toggleTipId}
              open={openToggleTip}
              anchorEl={toggletipAnchor}
              onClose={handleCloseConfigToggletip}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              sx={customStyles.popover}
            >
              <Box sx={customStyles.toggletip}>
                <FormControl component='fieldset'>
                  <Box sx={customStyles.toggletipInfo}>{lang.form_builder.commentsFormLabel}</Box>
                  <FormGroup>
                    {question.options.map((option: any, index: number) => {
                      return (
                        <Box
                          key={`optionReq-${index}`}
                          sx={customStyles.commentOptionRow}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                disableTouchRipple
                                size={'small'}
                                color={'default'}
                                checked={option.req_text || option.req_image || option.req_finding}
                                onChange={() =>
                                  handleObligatoryCommentInTableCheckbox(tableId, index, option)
                                }
                                name={option.option}
                              />
                            }
                            label={option.option}
                            sx={customStyles.toggletipLabel}
                          />
                          <Box sx={customStyles.commentAlternatives}>
                            <Tooltip title={lang.form_builder.textCommentBtnTooltip}>
                              <IconButton
                                size='small'
                                onClick={() =>
                                  onObligatoryOptionCommentInTableChange(
                                    tableId,
                                    question.id,
                                    index,
                                    !option.req_text,
                                    option.req_image,
                                    option.req_finding,
                                  )
                                }
                              >
                                <TextQuestionIcon
                                  sx={
                                    {
                                      ...customStyles.commentAlternativeBtn,
                                      ...customStyles.commentTextBtn,
                                      ...(option.req_text && customStyles.commentTextRequired),
                                    } as SxProps<Theme>
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                safeToReqImage
                                  ? lang.form_builder.imageCommentBtnTooltip
                                  : lang.form_builder.imageCommentBtnTooltipDisabled
                              }
                            >
                              <span>
                                <IconButton
                                  size='small'
                                  disabled={!safeToReqImage}
                                  onClick={() =>
                                    onObligatoryOptionCommentInTableChange(
                                      tableId,
                                      question.id,
                                      index,
                                      option.req_text,
                                      !option.req_image,
                                      option.req_finding,
                                    )
                                  }
                                >
                                  <PhotoQuestionIcon
                                    stroke={!option.req_image ? '#B8BABF' : '#3B3C40'}
                                    sx={
                                      {
                                        ...customStyles.commentAlternativeBtn,
                                        ...customStyles.commentImageBtn,
                                      } as SxProps<Theme>
                                    }
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {findingsActivated && findingsCommentRequiredFlag && (
                              <Tooltip title={lang.form_builder.findingCommentBtnTooltip}>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    onObligatoryOptionCommentInTableChange(
                                      tableId,
                                      question.id,
                                      index,
                                      option.req_text,
                                      option.req_image,
                                      !option.req_finding,
                                    )
                                  }
                                >
                                  <AlertTriangleOutline
                                    sx={
                                      {
                                        ...customStyles.commentAlternativeBtn,
                                        ...customStyles.commentTextBtn,
                                        ...(option.req_finding && customStyles.commentTextRequired),
                                      } as SxProps<Theme>
                                    }
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </FormGroup>
                </FormControl>
              </Box>
            </Popover>
          </Box>
        )}
        {checkFlag('ChecklistConfig') && question.type === 'select_option_metadata_comments' && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.disableGallery}
                onChange={() =>
                  onChecklistSettingsChange(
                    question.id,
                    !question.disableGallery,
                    checklistMaxImages,
                    tableId,
                  )
                }
              />
              <Box sx={customStyles.label}>{lang.form_builder.disableGallery}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <TextField
                type='number'
                sx={customStyles.textFieldMargin}
                value={checklistMaxImages}
                defaultValue={1}
                onChange={(event) => {
                  handleOnChecklistSettingsChange(event, tableId);
                }}
                InputProps={{
                  inputProps: { min: 1, max: 10, sx: customStyles.textFieldInputPadding },
                }}
              />
              <Box sx={customStyles.label}>{lang.form_builder.maxImagesPerItem}</Box>
            </Box>
          </>
        )}
        {checkFlag('RepeatableQuestionSkipLogic') && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.skipLogic === '1'}
              onChange={handleSkipLogicChange}
            />
            <Box sx={customStyles.label}>{lang.form_builder.skipQuestions}</Box>
            <Dialog
              open={openedAlert}
              maxWidth={'xs'}
              onClose={() => setOpenedAlert(false)}
            >
              <DialogContent>
                <DialogContentText>
                  <Box sx={{ textAlign: 'center', fontSize: 14 }}>
                    {lang.form_builder.payingAlert}
                  </Box>
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                  onClick={() => setOpenedAlert(false)}
                  color={'primary'}
                  size={'small'}
                  sx={{ textTransform: 'capitalize' }}
                  variant={'outlined'}
                >
                  {lang.form_builder.ok}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
        {question.type === 'date' && dateQuestionValidationFlag && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.dateValidationType !== undefined}
              onChange={() => onDateValidationCheckboxTableChange(question.id, tableId)}
            />
            <Box sx={customStyles.label}>{lang.form_builder.dateValidation.addValidation}</Box>
          </Box>
        )}
      </Box>
      {checkFlag('RepeatableQuestionSkipLogic') && question.skipLogic === '1' && (
        <SkipLogicComponent
          questionId={question.id}
          tableId={tableId}
          skipLogicQuestion={question.skipLogicQuestion}
          skipLogicType={question.skipLogicType}
          skipLogicValue={question.skipLogicValue}
          skipLogicTarget={question.skipLogicTarget}
          onSkipSettingChange={onSkipSettingInTableChange}
          logicQuestions={tableLogicQuestions[tableId] || []}
        />
      )}
      {question.type === 'multi_photo' && (
        <Box sx={customStyles.mobileOnlyAlert}>
          <InfoAlert message={lang.form_builder.mobileOnlyAlert} />
        </Box>
      )}
    </CardContent>
  );
});

export { TableQuestionSetting };
